import React, { useCallback, useState } from "react";
import { isEmpty, isEqual } from "lodash";
import { createStructuredSelector } from "reselect";
import { connect } from "react-redux";

import { required, requiredCodeRef, getValidation, getDecimalPlaces } from "utils/fieldValidation";
import { useModelCodeLib } from "utils/codes/modelCodeLib";
import { getBaseUnits, getOptions, getUnitOptions } from "utils/fields";
import { useStateSelector } from "utils/field";
import { actions } from "features/Model/Enclosure/_ducks";

import Field from "components/Field";
import InputRow from "components/Input/Row";
import InputWithUnits from "components/Input/InputWithUnits";
import Tooltip from "components/Tooltip";
import Select from "components/Input/Select";
import Checkbox from "components/Input/Checkbox";

import Add from "assets/images/icons/JSX/Add";
import Book from "assets/images/icons/JSX/Book";
import Edit from "assets/images/icons/JSX/Edit";

import classes from "./style.module.scss";
import globalStyles from "components/globalStyles.module.scss";
import { isOrgCodeRef } from "utils/codes/refs";

const { setInitCode, updateCodeDrawer } = actions;

const wallTypeValidation = [required, ...getValidation("wallInsType")];
const lintelTypeValidation = getValidation("wallLintelInsType");
const orientationValidation = getValidation("wallDirection");

export const CodeFooter = ({ toggleDrawer, setInitCode, codeType }) => (
    <div
        className={classes.codeFooter}
        onClick={async () => {
            await setInitCode({}, codeType);
            toggleDrawer(true);
        }}
    >
        <span>Create New Code</span>
        <Add />
    </div>
);

const codeSort = (a, b) => {
    if (a.label < b.label) {
        return -1;
    }
    if (a.label > b.label) {
        return 1;
    }
    return 0;
};

const LabelActionComponent = ({ codeRef, handleEdit, codeType }) => (
    <>
        <div
            className={globalStyles.editCode}
            data-tip="Edit Code"
            data-for={codeRef}
            onClick={(event) => handleEdit(event, codeRef, codeType)}
        >
            <Edit />
            <Tooltip id={codeRef} className={globalStyles.fieldTooltip} place="left" />
        </div>
    </>
);

// Wall Ins. Type codes found in code library
export const wallTypeLibCodesMap = (libCodes, handleEditLibWallCode) =>
    Object.keys(libCodes)
        .map((codeRef) => {
            const { label = "" } = libCodes[codeRef] || {};
            const [standardCode] = codeRef.split("Wall-U");

            return {
                label: label,
                labelIcon: Book,
                labelTag: isOrgCodeRef(codeRef) ? "Organization" : !standardCode ? "User Defined" : "",
                labelActionComponent: () =>
                    handleEditLibWallCode ? (
                        <LabelActionComponent
                            codeRef={codeRef}
                            handleEdit={handleEditLibWallCode}
                            codeType={standardCode ? "S" : "U"}
                        />
                    ) : null,
                value: {
                    codeLabel: label,
                    codeRef,
                },
            };
        })
        .sort(codeSort);

// Wall Ins. Type codes found in model, filtered by those in library
export const wallTypeModelCodesMap = (modelCodes, libCodes, handleEditModelWallCode) =>
    Object.values(modelCodes)
        .filter(({ codeRef }) => {
            if (!codeRef) return false;
            const [component] = codeRef.split("-");
            if (!!libCodes[codeRef]) {
                return (
                    (!libCodes[codeRef] && component === "Wall") ||
                    !isEqual(modelCodes[codeRef]?.layers, libCodes[codeRef]?.layers)
                );
            }
            return !libCodes[codeRef] && component === "Wall";
        })
        .map(({ label, codeRef }) => {
            const [standardCode] = codeRef.split("Wall-U");

            return {
                label,
                labelTag: isOrgCodeRef(codeRef) ? "Organization" : !standardCode ? "User Defined" : "",
                labelActionComponent: () =>
                    handleEditModelWallCode ? (
                        <LabelActionComponent
                            codeRef={codeRef}
                            handleEdit={handleEditModelWallCode}
                            codeType={standardCode ? "S" : "U"}
                        />
                    ) : null,
                value: {
                    codeLabel: label,
                    codeRef,
                },
            };
        })
        .sort(codeSort);

// Lintel Type codes found in code library
const lintelTypeLibCodesMap = (libLintelCodes, handleEditLibLintelCode) =>
    Object.keys(libLintelCodes)
        .map((codeRef) => {
            const { label = "" } = libLintelCodes[codeRef] || {};
            const [standardCode] = codeRef.split("Lintel-U");

            return {
                label: label,
                labelIcon: Book,
                labelActionComponent: standardCode
                    ? () => <LabelActionComponent codeRef={codeRef} handleEdit={handleEditLibLintelCode} />
                    : null,
                value: {
                    codeLabel: label,
                    codeRef,
                },
            };
        })
        .sort(codeSort);

// Lintel Type codes found in model, filtered by those in library
const lintelTypeModelCodesMap = (modelCodes, libCodes, handleEditModelLintelCode) =>
    Object.values(modelCodes)
        .filter(({ codeRef }) => {
            if (!codeRef) return false;
            const [component] = codeRef.split("-");
            if (!!libCodes[codeRef]) {
                return (
                    (!libCodes[codeRef] && component === "Lintel") ||
                    !isEqual(modelCodes[codeRef]?.layers, libCodes[codeRef]?.layers)
                );
            }
            return !libCodes[codeRef] && component === "Lintel";
        })
        .map(({ label, codeRef }) => {
            const [standardCode] = codeRef.split("Lintel-U");

            return {
                label,
                labelActionComponent: standardCode
                    ? () => <LabelActionComponent codeRef={codeRef} handleEdit={handleEditModelLintelCode} />
                    : null,
                value: {
                    codeLabel: label,
                    codeRef,
                },
            };
        })
        .sort(codeSort);

const Construction = React.memo(
    ({
        id,
        accessor,
        change,
        selectedUpgrade,
        canBeEditedFromParametric = true,
        disabledCodeEdit = false,
        isCompact = false,
        setInitCode,
        updateCodeDrawer,
        isParametricAnalysis = false,
        isCommunity = false,
        codeRefs = [],
        isInterior = false,
    }) => {
        const [isTouched, setIsTouched] = useState(false); // Hide warning when code is reselected but not yet added to ecmInputMap

        const { Wall: { codes: libCodes = {} } = {}, Lintel: { codes: libLintelCodes = {} } = {} } =
            useModelCodeLib() || {};
        const { calculatingRValue = {} } = useStateSelector(`enclosure`) || {};
        const { currentPackage = {} } = useStateSelector(`upgradeLibrary`) || {};
        const { modelData = {} } = useStateSelector(`model`) || {};
        const { parametricAnalysis = {} } = useStateSelector(`parametricAnalysis`) || {};
        const { baseModelId = "" } = useStateSelector(`commUpgrades`) || {};
        const { community = {} } = useStateSelector(`community`) || {};

        const ecmInputMap = parametricAnalysis?.ecmInputMap || community?.ecmInputMap || {};

        const allEcmInputMapUpgradesArr = ecmInputMap
            ? Object.keys(ecmInputMap)
                .map((key) =>
                    Object.entries(ecmInputMap[key] ?? {})
                        .filter(([k, _]) => k !== "None")
                        .map(([k, v]) => ({ [k]: v }))
                )
                .flat()
            : [];

        const allEcmInputMapUpgrades = allEcmInputMapUpgradesArr.reduce((acc, curr) => ({ ...acc, ...curr }), {});

        const modelCodesRaw =
            allEcmInputMapUpgrades[selectedUpgrade]?.packageValues?.codes ||
            currentPackage?.upgrades?.[selectedUpgrade]?.codes ||
            modelData?.codes ||
            {};

        const modelCodes =
            modelCodesRaw && typeof modelCodesRaw === "object" && !Array.isArray(modelCodesRaw) ? modelCodesRaw : {};
        
        const addUserSpecifiedRVal = useStateSelector(`${accessor}.addUserSpecifiedRVal`) || false;
        const isAddUserSpecifiedRVal = isParametricAnalysis ? addUserSpecifiedRVal : false;
        const modelUnits = modelData?.uiSettings?.primaryUnits || {};
        const programMode = modelData?.program?.class || "";

        const { codeRef: selectedCodeRef } = useStateSelector(`${accessor}.wallInsType`) || {};
        const selectedNomRValue = useStateSelector(`${accessor}.wallInsType_nomRVal`) || 0;
        const wallCodeWarning = useStateSelector(`${accessor}.wallInsType_warning`) || "";
        const { codeRef: selectedLintelCodeRef } = useStateSelector(`${accessor}.lintelInsType`) || {};
        const rValUnits = useStateSelector(`${accessor}.wallInsType_u`);
        const { [id]: { wallInsType: isCalculatingRValue = false } = {} } = calculatingRValue;

        //Determine if selectedCodeRef is in modelCodes (attached properly)
        const modelCodesContainsSelected =
            Object.keys(modelCodes).includes(selectedCodeRef) || selectedCodeRef === "UserSpecified" || isTouched;

        const { id: facingId } = useStateSelector(`${accessor}.facingDirection`) || {};

        const isErsAndUSpec =
            selectedCodeRef === "UserSpecified" && (programMode.includes("ERS") || programMode.includes("ONrh"));

        const handleTypeChange = useCallback(
            ({ codeRef = "" }) => {
                if (!codeRef) return;
                if (codeRef === "UserSpecified") {
                    change(`${accessor}.wallInsType_nomRVal`, selectedNomRValue || 0.1);
                    change(`${accessor}.wallInsType_effRVal`, selectedNomRValue || 0.1);
                    change(`${accessor}.wallInsType_warning`, "");
                    return;
                }

                const modelMatch = modelCodes[codeRef] || {};
                if (!isEmpty(modelMatch)) {
                    change(`${accessor}.wallInsType_nomRVal`, modelMatch.nominalRValue || 0.1);
                    change(`${accessor}.wallInsType_effRVal`, modelMatch.nominalRValue || 0.1);
                    change(`${accessor}.wallInsType_warning`, modelMatch.warningType || "");
                } else {
                    const { nominalRValue = 0, warningType = "" } = libCodes[codeRef] || {};
                    change(`${accessor}.wallInsType_nomRVal`, nominalRValue || 0.1);
                    change(`${accessor}.wallInsType_effRVal`, nominalRValue || 0.1);
                    change(`${accessor}.wallInsType_warning`, warningType || "");

                    if (isCommunity || isParametricAnalysis) {
                        setIsTouched(true);
                    } else {
                        change(`model.modelData.codes.${codeRef}`, { ...libCodes[codeRef], codeRef });
                    }
                }

                change(`model.stashedCodes.Wall.${accessor.split(".").slice(-1)[0]}`, codeRef);
            },
            [libCodes, modelCodes, change, accessor, isCommunity, isParametricAnalysis]
        );

        const handleLintelTypeChange = useCallback(
            ({ codeRef = "" }) => {
                if (!codeRef) return;
                if (codeRef === "UserSpecified") {
                    return;
                }

                const modelMatch = modelCodes[codeRef] || {};
                if (isEmpty(modelMatch)) {
                    if (isCommunity) {
                        change(`community.community.modelData.models.${baseModelId}.codes.${codeRef}`, { ...libLintelCodes[codeRef], codeRef });
                    } else {
                        change(`model.modelData.codes.${codeRef}`, { ...libLintelCodes[codeRef], codeRef });
                    }
                }

                change(`model.stashedCodes.Lintel.${accessor.split(".").slice(-1)[0]}`, codeRef);
            },
            [change, accessor, modelCodes, libLintelCodes, isCommunity, baseModelId]
        );

        const handleEditModelWallCode = useCallback(
            async (event, codeRef, codeType) => {
                event.stopPropagation(); //Don't let click event bubble up to parent
                const initCodeType = codeType === "U" ? "udefStandard" : "wallInsType";
                const code = modelCodes[codeRef];
                await setInitCode(code, initCodeType);
                updateCodeDrawer({
                    isOpen: true,
                    codeName: "Wall",
                    isEditing: true,
                    componentType: "Wall",
                    codeType: codeType,
                    fieldAccessor: `${accessor}.wallInsType`,
                    componentId: id,
                    id: codeRef,
                    lastEdited: code.lastEdited || "",
                });
            },
            [accessor, id, modelCodes, setInitCode, updateCodeDrawer]
        );

        const handleEditLibWallCode = disabledCodeEdit
            ? null
            : useCallback(
                async (event, codeRef, codeType) => {
                    event.stopPropagation(); //Don't let click event bubble up to parent
                    const initCodeType = codeType === "U" ? "udefStandard" : "wallInsType";
                    const code = {
                        ...libCodes[codeRef],
                        codeRef: codeRef,
                        isLibCode: true,
                    };
                    await setInitCode(code, initCodeType);
                    updateCodeDrawer({
                        isOpen: true,
                        codeName: "Wall",
                        isEditing: true,
                        componentType: "Wall",
                        codeType: codeType,
                        fieldAccessor: `${accessor}.wallInsType`,
                        componentId: id,
                        id: codeRef,
                        lastEdited: code.lastEdited || "",
                    });
                },
                [libCodes, setInitCode, updateCodeDrawer, accessor, id]
            );

        const handleEditModelLintelCode = useCallback(
            async (event, codeRef) => {
                event.stopPropagation(); //Don't let click event bubble up to parent
                const code = modelCodes[codeRef];
                await setInitCode(code, "lintelInsType");
                updateCodeDrawer({
                    isOpen: true,
                    codeName: "Lintel",
                    isEditing: true,
                    componentType: "Lintel",
                    codeType: "S",
                    fieldAccessor: `${accessor}.lintelInsType`,
                    componentId: id,
                    id: codeRef,
                    lastEdited: code.lastEdited || "",
                });
            },
            [accessor, id, modelCodes, setInitCode, updateCodeDrawer]
        );

        const handleEditLibLintelCode = useCallback(
            async (event, codeRef) => {
                event.stopPropagation(); //Don't let click event bubble up to parent
                const code = {
                    ...libLintelCodes[codeRef],
                    codeRef: codeRef,
                    isLibCode: true,
                };
                await setInitCode(code, "lintelInsType");
                updateCodeDrawer({
                    isOpen: true,
                    codeName: "Lintel",
                    isEditing: true,
                    componentType: "Lintel",
                    codeType: "S",
                    fieldAccessor: `${accessor}.lintelInsType`,
                    componentId: id,
                    id: codeRef,
                    lastEdited: code.lastEdited || "",
                });
            },
            [accessor, id, libLintelCodes, setInitCode, updateCodeDrawer]
        );

        return (
            <>
                <InputRow gridTemplate={isCompact ? "1fr 1fr 1fr 1fr" : "1fr 1fr 1fr"} compact={isCompact}>
                    <Field
                        component={Select}
                        type="number"
                        name={`${accessor}.wallInsType`}
                        options={[
                            {
                                label: "User Specified",
                                value: {
                                    codeLabel: "User Specified",
                                    codeRef: "UserSpecified",
                                },
                            },
                            ...wallTypeModelCodesMap(modelCodes, libCodes, handleEditModelWallCode),
                            ...wallTypeLibCodesMap(libCodes, handleEditLibWallCode),
                        ].map((option) => ({
                            ...option,
                            disabled: codeRefs.includes(option.value.codeRef),
                        }))}
                        label="Wall Type"
                        placeholder="Choose Wall Type"
                        searchPlaceholder="Search Wall Codes"
                        search={true}
                        footer={() => (
                            <CodeFooter
                                toggleDrawer={(value) =>
                                    updateCodeDrawer({
                                        isOpen: value,
                                        codeName: "Wall",
                                        isEditing: false,
                                        componentType: "Wall",
                                        codeType: "S",
                                        fieldAccessor: `${accessor}.wallInsType`,
                                        componentId: id,
                                    })
                                }
                                setInitCode={setInitCode}
                                codeType="wallInsType"
                            />
                        )}
                        className={classes.wallType}
                        isLoading={isCalculatingRValue}
                        onChange={handleTypeChange}
                        validate={requiredCodeRef}
                        disabled={!canBeEditedFromParametric}
                        compact={isCompact}
                        warning={!modelCodesContainsSelected ? "codeDataMissing" : null}
                    />
                    {selectedCodeRef === "UserSpecified" ? (
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.wallInsType_effRVal`}
                            label={"Effective R-Value"}
                            placeholder={0.0}
                            disabled={selectedCodeRef !== "UserSpecified" || !canBeEditedFromParametric}
                            validate={wallTypeValidation}
                            setTouched={true}
                            decimals={getDecimalPlaces("wallInsType")}
                            change={change}
                            setValue={selectedNomRValue || 0.1} // selectedNomRValue is updated to equal wallInsType_effRVal
                            units={{
                                base: getBaseUnits("wallInsType", modelUnits),
                                options: getUnitOptions("thermalResistance"),
                                selected: rValUnits,
                                unitType: "thermalResistance",
                                accessor: `${accessor}.wallInsType_u`,
                            }}
                            onChange={(value) => {
                                if (selectedCodeRef === "UserSpecified") {
                                    change(`${accessor}.wallInsType_nomRVal`, value);
                                }
                            }}
                            info={"User specified values are assumed to be effective R-values."}
                            warning={isErsAndUSpec ? "ersUSpecRVal" : ""}
                            compact={isCompact}
                        />
                    ) : (
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.wallInsType_nomRVal`}
                            label={"Nominal R-Value"}
                            placeholder={0.0}
                            disabled={true}
                            validate={wallTypeValidation}
                            setTouched={true}
                            decimals={getDecimalPlaces("wallInsType")}
                            change={change}
                            setValue={selectedNomRValue || 0.1}
                            units={{
                                base: getBaseUnits("wallInsType", modelUnits),
                                options: getUnitOptions("thermalResistance"),
                                selected: rValUnits,
                                unitType: "thermalResistance",
                                accessor: `${accessor}.wallInsType_u`,
                            }}
                            info={
                                "Values calculated by the Code Selector are nominal R-values. HOT2000 re-calculates R-values based on the content of the code when running a simulation."
                            }
                            warning={wallCodeWarning}
                            isLoading={isCalculatingRValue}
                            compact={isCompact}
                        />
                    )}
                    <Field
                        component={Select}
                        type="number"
                        name={`${accessor}.lintelInsType`}
                        options={[
                            {
                                label: "N/A",
                                value: {},
                            },
                            ...lintelTypeModelCodesMap(modelCodes, libLintelCodes, handleEditModelLintelCode),
                            ...lintelTypeLibCodesMap(libLintelCodes, handleEditLibLintelCode),
                        ]}
                        label="Lintel Type"
                        placeholder="Choose Lintel Type"
                        searchPlaceholder="Search Lintel Codes"
                        search={true}
                        footer={() => (
                            <CodeFooter
                                toggleDrawer={(value) =>
                                    updateCodeDrawer({
                                        isOpen: value,
                                        codeName: "Lintel",
                                        isEditing: false,
                                        componentType: "Lintel",
                                        codeType: "S",
                                        fieldAccessor: `${accessor}.lintelInsType`,
                                        componentId: id,
                                    })
                                }
                                setInitCode={setInitCode}
                                codeType="lintelInsType"
                            />
                        )}
                        onChange={handleLintelTypeChange}
                        className={classes.lintelType}
                        info={"If unknown, use code label 100"}
                        disabled={!canBeEditedFromParametric}
                        // validate={requiredCodeRef}
                        compact={isCompact}
                    />
                    {isCompact && (
                        <>
                            <Field
                                component={Select}
                                name={`${accessor}.facingDirection`}
                                options={getOptions({ fieldKey: "wallDirection" }).map((option) => ({
                                    ...option,
                                    disabled: codeRefs.includes(option.value?.codeRef),
                                }))}
                                label="Facing Direction"
                                placeholder="Choose Facing Direction"
                                validate={orientationValidation}
                                compact
                                compassProps={{
                                    facingId: facingId,
                                    disabled: [0, 9].includes(facingId),
                                    rotationOptions: {
                                        0: 0,
                                        1: 180,
                                        2: 135,
                                        3: 90,
                                        4: 45,
                                        5: 0,
                                        6: -45,
                                        7: -90,
                                        8: -135,
                                        9: 0,
                                    },
                                }}
                            />
                        </>
                    )}
                </InputRow>
                {isParametricAnalysis && (
                    <>
                        <InputRow gridTemplate="1fr" compact lastRow>
                            <Field
                                className={classes.adjacentCheckbox}
                                component={Checkbox}
                                name={`${accessor}.addUserSpecifiedRVal`}
                                label="Add User Specified R-Value to All Components"
                                large
                                type="checkbox"
                                compact
                                onChange={(value) => {
                                    if (value) {
                                        change(`${accessor}.wallInsType`, {
                                            codeRef: "UserSpecified",
                                            codeLabel: "User Specified",
                                        });
                                    }
                                }}
                                disabled={!canBeEditedFromParametric}
                            />
                        </InputRow>
                        {isAddUserSpecifiedRVal && (
                            <div
                                style={{
                                    backgroundColor: "aliceblue",
                                    borderRadius: "10px",
                                    padding: "0.75rem",
                                    lineHeight: "normal",
                                    fontSize: "small",
                                    marginTop: "1.25rem",
                                }}
                            >
                                This user specified effective R-value will be added to all wall components in the model
                                individually. The resulting wall R-values may not be equal, depending on their starting
                                points.
                                <br />
                                <br />
                                The initial effective R-value for each component is determined using the parallel path
                                calculation method, aligned with F280 and the National Building Code, and may differ
                                slightly from HOT2000's calculation method.
                            </div>
                        )}
                    </>
                )}
            </>
        );
    }
);

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = (dispatch) => ({
    setInitCode: async (code, type) => {
        return dispatch(setInitCode(code, type));
    },
    updateCodeDrawer: (updates) => dispatch(updateCodeDrawer({ updates })),
});

export default connect(mapStateToProps, mapDispatchToProps)(Construction);
